import { inject } from '@angular/core';
import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';
import { injectDispatch } from '@ngrx/signals/events';
import { AuthDataService, authEvents, AUTH_TOKEN_KEYS } from '@vantage-web/core-data';
import { SKIP_INTERCEPTOR } from './skip-interceptor.token';

/** Marks a request as already having attempted a token refresh — prevents infinite loops. */
const AUTH_REFRESH_RETRIED = new HttpContextToken<boolean>(() => false);

/**
 * Attaches the Cognito IdToken as a Bearer Authorization header.
 *
 * Refresh/logout is scoped to a genuine **401** only (the server rejected the token): we attempt a
 * single token refresh via the shared refreshSession() observable and retry the original request; if
 * the refresh fails the session is genuinely dead, so we dispatch logout and re-surface the original
 * error. Any other status (403/422/4xx/5xx) is a failure of that one request — not a dead session —
 * and passes straight through with the session intact, so e.g. a 403 on a create never logs the user out.
 *
 * Concurrent 401s share a single in-flight refresh call (via shareReplay in AuthDataService).
 */
export function cognitoAuthInterceptor(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
  if (req.context.get(SKIP_INTERCEPTOR)) {
    return next(req);
  }

  const authService = inject(AuthDataService);
  const dispatch = injectDispatch(authEvents);

  const idToken = localStorage.getItem(AUTH_TOKEN_KEYS.idToken);

  const authReq = idToken
    ? req.clone({ setHeaders: { Authorization: `Bearer ${idToken}` } })
    : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Don't retry if this request already attempted a refresh
      if (req.context.get(AUTH_REFRESH_RETRIED)) {
        return throwError(() => error);
      }

      // Only a genuine 401 — the server rejected the token — warrants a refresh and a
      // session-ending logout. A 403/422/4xx/5xx is a failure of THIS request, not a dead
      // session, and must surface to the caller untouched. (Previously a stale-looking local
      // idToken pulled any error into this branch, so e.g. a 403 logged the user out.)
      const refreshToken = localStorage.getItem(AUTH_TOKEN_KEYS.refreshToken);

      if (error.status === 401 && refreshToken) {
        return from(authService.refreshSession()).pipe(
          switchMap((output) => {
            const result = output.AuthenticationResult;
            if (result && authService.saveSession(result)) {
              const newToken = localStorage.getItem(AUTH_TOKEN_KEYS.idToken);
              return next(
                req.clone({
                  setHeaders: { Authorization: `Bearer ${newToken}` },
                  context: req.context.set(AUTH_REFRESH_RETRIED, true),
                }),
              );
            }
            return throwError(() => error);
          }),
          catchError(() => {
            // Refresh failed on a real 401 → the session is dead. Log out, but re-throw the
            // ORIGINAL error so the caller can react to what actually failed.
            dispatch.logout();
            return throwError(() => error);
          }),
        );
      }

      return throwError(() => error);
    }),
  );
}
